import { useEffect, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { FileText, Download, LogOut, Wallet, CalendarClock } from "lucide-react";

const INR = (n) =>
  "₹ " + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch { return iso; }
};

export default function MyOffboarding() {
  const { user, logout } = useAuth();
  const [info, setInfo] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/my-offboarding"),
        api.get("/my-offboarding-documents"),
      ]);
      setInfo(a.data);
      setDocs(b.data || []);
    } catch (e) {
      toast.error(formatError(e));
      setInfo(null);
      setDocs([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const download = async (doc) => {
    try {
      const rid = info.resignation_id;
      const resp = await api.get(`/resignations/${rid}/documents/${doc.id}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || "document.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[var(--pk-accent)] grid place-items-center text-white font-bold heading text-sm">PV</div>
            <div>
              <div className="heading font-bold text-[15px] leading-tight">Panchakosh Ventures</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Ex-Employee Portal</div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => { await logout(); window.location.href = "/login"; }}
            data-testid="my-offb-logout"
          >
            <LogOut size={14} className="mr-1.5" /> Sign out
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6" data-testid="my-offb-page">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">Welcome back</div>
          <h1 className="heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            Your Offboarding Records
          </h1>
          <p className="text-slate-600 text-sm mt-2">
            This is a read-only portal where you can download your offboarding documents and view your
            full & final settlement status. Access is valid only until your temporary credential
            expires.
          </p>
        </div>

        {loading ? (
          <div className="text-slate-400 text-sm py-10 text-center">Loading…</div>
        ) : !info ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-800 px-4 py-3 text-sm">
            Your temporary access could not be loaded. Please contact HR.
          </div>
        ) : (
          <>
            {/* Profile summary */}
            <div className="bg-white rounded-lg border border-slate-200 p-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-[12.5px]">
                <KV k="Name" v={info.employee_name} />
                <KV k="Employee Code" v={info.employee_code} mono />
                <KV k="Designation" v={info.designation} />
                <KV k="Department" v={info.department} />
                <KV k="Date of Joining" v={fmtDate(info.date_of_joining)} mono />
                <KV k="Last Working Day" v={fmtDate(info.last_working_day)} mono />
                <KV k="Access Expires On" v={fmtDate(info.temp_access_expires_at)} mono />
              </div>
            </div>

            {/* FNF amount */}
            <div className="bg-white rounded-lg border-2 border-emerald-200 p-5" data-testid="my-offb-fnf">
              <div className="flex items-center gap-2 mb-2">
                <Wallet size={16} className="text-emerald-700" />
                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                  Full & Final Settlement
                </div>
              </div>
              <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                  <div className="mono text-3xl font-extrabold text-emerald-700">
                    {INR(info.fnf_amount)}
                  </div>
                  {info.fnf_disbursed ? (
                    <Badge className="bg-emerald-100 text-emerald-800 mt-2">
                      <CalendarClock size={11} className="mr-1" />
                      Disbursed on {fmtDate(info.fnf_disbursed_date)}
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-800 mt-2">
                      Pending Disbursement
                    </Badge>
                  )}
                </div>
                <div className="text-[11.5px] text-slate-500 max-w-xs">
                  This is the net amount payable to you after deductions and adjustments. Please
                  contact HR if you have any queries about the computation.
                </div>
              </div>
            </div>

            {/* Documents */}
            <div className="bg-white rounded-lg border border-slate-200 p-5">
              <div className="flex items-center gap-2 mb-3">
                <FileText size={16} className="text-violet-700" />
                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                  Your Offboarding Documents
                </div>
              </div>
              {docs.length === 0 ? (
                <div className="text-slate-400 text-sm py-10 text-center italic">
                  No documents have been issued to you yet. Please check back later.
                </div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {docs.map((d) => (
                    <li key={d.id} className="py-3 flex items-center justify-between gap-3" data-testid={`my-offb-doc-${d.doc_type}`}>
                      <div>
                        <div className="font-semibold text-slate-800 text-[13.5px]">{d.doc_type_label}</div>
                        <div className="text-[10.5px] text-slate-500 mono">
                          {d.filename} · {(d.size_bytes / 1024).toFixed(1)} KB · {d.kind === "uploaded" ? "Signed copy" : "Generated"}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => download(d)}
                        className="bg-violet-600 hover:bg-violet-700 h-8 text-[11.5px]"
                        data-testid={`my-offb-download-${d.doc_type}`}
                      >
                        <Download size={12} className="mr-1.5" /> Download
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="text-[11px] text-slate-400 italic">
              Thank you for being part of Panchakosh Ventures. Wishing you the very best ahead.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function KV({ k, v, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{k}</div>
      <div className={`text-slate-800 mt-0.5 ${mono ? "mono" : ""}`}>{v || "—"}</div>
    </div>
  );
}
